/**
 * "Start your shift": everything a till needs before the first sale, in one
 * place and in the order it is done.
 *
 * Getting a till ready used to be three errands from three rows of the tablet
 * menu: clock in under "Your shift", say which till this is under "This
 * till", open the drawer under "Cash drawer". The header said "Not clocked
 * in · Choose this till" and left the barista to work out where each one was.
 * Now the header offers one button while anything is left to do, and this
 * sheet lists only what is: a step already done shows as done, and a step
 * this person or this site does not have (an owner not on the payroll, a site
 * with no registered tills, somebody who does not take cash) is not shown.
 */

import { useState, type ReactNode } from 'react';
import {
  formatMoney,
  useClockIn,
  useMyShift,
  useOpenDrawer,
  usePermission,
} from '@restaurant/api-client';
import { ActionError, Button, NumberPad, Sheet } from '@restaurant/ui';

import { TillChoices } from './drawer';
import { FLOATS, useShiftSteps, type ShiftSteps } from './shift-status';
import { useThisTill } from './this-till';

/** The header's offer while anything is left to do. */
export function StartShiftButton({ steps, onOpen }: { steps: ShiftSteps; onOpen: () => void }) {
  if (!steps.ready || steps.left === 0) return null;
  return (
    <button type="button" className="sh__start" onClick={onOpen}>
      Start your shift
      <span className="sh__start-count">
        {steps.left} {steps.left === 1 ? 'step' : 'steps'}
      </span>
    </button>
  );
}

export function StartShiftSheet({
  locationId,
  onDismiss,
}: {
  locationId: string | undefined;
  onDismiss: () => void;
}) {
  const steps = useShiftSteps(locationId);
  const shift = useMyShift();
  const clockIn = useClockIn();
  const till = useThisTill(locationId);
  const mayOpen = usePermission('payments.drawer.open');
  const open = useOpenDrawer();
  const [float, setFloat] = useState('');

  const allDone = steps.ready && steps.left === 0;

  return (
    <Sheet
      title={allDone ? 'Ready for the first sale' : 'Start your shift'}
      onDismiss={onDismiss}
      actions={
        allDone ? (
          <Button variant="primary" size="lg" block onClick={onDismiss}>
            Done
          </Button>
        ) : null
      }
    >
      <ol className="sh__steps">
        {steps.clock !== 'skip' && shift.data?.employee_id && (
          <Step done={steps.clock === 'done'} title="Clock in">
            {steps.clock === 'done' ? (
              <p className="sh__said">{shift.data.name} is on the clock.</p>
            ) : (
              <>
                <Button
                  variant="primary"
                  size="lg"
                  block
                  disabled={!locationId}
                  loading={clockIn.isPending}
                  onClick={() =>
                    locationId &&
                    clockIn.mutate({
                      employeeId: shift.data?.employee_id as string,
                      locationId,
                    })
                  }
                >
                  Clock in as {shift.data.name}
                </Button>
                <ActionError error={clockIn.error} action="Clocking in" />
              </>
            )}
            {clockIn.data?.warnings && clockIn.data.warnings.length > 0 && (
              <div className="ck__warn" role="status">
                Clocked in. This site&apos;s rules say:
                <ul>
                  {clockIn.data.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </Step>
        )}

        {steps.till !== 'skip' && (
          <Step done={steps.till === 'done'} title="Which till is this?">
            {steps.till === 'done' && till.state.kind === 'chosen' ? (
              <p className="sh__said">This tablet is {till.state.till.name}.</p>
            ) : (
              <>
                <p className="sh__hint">
                  Asked once and remembered on this tablet: each till has its own drawer.
                </p>
                <TillChoices locationId={locationId} />
              </>
            )}
          </Step>
        )}

        {steps.drawer !== 'skip' && (
          <Step done={steps.drawer === 'done'} title="Open the drawer">
            {steps.drawer === 'done' ? (
              <p className="sh__said">The drawer is open.</p>
            ) : steps.till === 'todo' ? (
              <p className="sh__hint">Choose the till first: the drawer is that till&apos;s.</p>
            ) : mayOpen ? (
              <>
                <p className="sh__hint">Count the float going in, then open it.</p>
                <div className="dr__presets">
                  {FLOATS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      className="pay__note tabular"
                      aria-pressed={float === preset}
                      onClick={() => setFloat(preset)}
                    >
                      {formatMoney(preset)}
                    </button>
                  ))}
                </div>
                <NumberPad label="Float" value={float} onChange={setFloat} />
                <Button
                  variant="primary"
                  size="lg"
                  block
                  disabled={!locationId}
                  loading={open.isPending}
                  onClick={() =>
                    locationId &&
                    open.mutate({
                      location_id: locationId,
                      opening_float: float.trim() || '0',
                      ...(till.terminalId ? { terminal_id: till.terminalId } : {}),
                    })
                  }
                >
                  Open with {formatMoney(float.trim() || '0')} float
                </Button>
                <ActionError error={open.error} action="Opening the drawer" />
              </>
            ) : (
              <p className="sh__hint">
                Opening the drawer is for a cashier or a manager. Cards, vouchers and accounts
                work without it; cash waits until it is open.
              </p>
            )}
          </Step>
        )}
      </ol>
    </Sheet>
  );
}

function Step({
  done,
  title,
  children,
}: {
  done: boolean;
  title: string;
  children: ReactNode;
}) {
  return (
    <li className={`sh__step${done ? ' is-done' : ''}`}>
      <span className="sh__mark" aria-hidden="true">
        {done ? '✓' : ''}
      </span>
      <div className="sh__body">
        <h3 className="sh__title">
          {title}
          {done && <span className="sr-only"> (done)</span>}
        </h3>
        {children}
      </div>
    </li>
  );
}
